import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  TextInput,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  Spinner,
  EditIcon,
  Alert,
} from '@razorpay/blade/components';
import { WorkflowCanvas } from '../WorkflowCanvas';
import { RightPanel } from '../RightPanel';
import { RightPanelContainer } from '../WorkflowLayout';
import {
  AIWorkflowConfig,
  WorkflowStep,
  DecisionNode,
  RightPanelState,
  StepType,
  TriggerConfig,
  ActionConfig,
  TriggerStepData,
  ActionStepData,
} from '../../types';
import api from 'api';
import { aiWorkflowQueryKeys } from 'api/aiWorkflows';
import {
  ApiTrigger,
  ApiAction,
  CreateWorkflowRequest,
} from 'api/types/aiWorkflowTypes';
import { AppError } from 'utils/AppError';
import { uniqueId } from 'utils/Numbers';
import { ClickableWrapper } from './styles';
import { createEmptyWorkflow, groupActions, convertToApiRequest } from './utils';

interface WorkflowBuilderProps {
  existingWorkflow?: AIWorkflowConfig;
  onSave: (workflow: AIWorkflowConfig) => void;
  onCancel: () => void;
}

const WorkflowBuilder = ({ existingWorkflow, onSave, onCancel }: WorkflowBuilderProps) => {
  const queryClient = useQueryClient();
  const [workflow, setWorkflow] = useState<AIWorkflowConfig>(
    existingWorkflow || createEmptyWorkflow()
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedStep, setSelectedStep] = useState<WorkflowStep | undefined>();
  const [panelState, setPanelState] = useState<RightPanelState>({ mode: 'select_trigger' });
  const [isEditingName, setIsEditingName] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    data: triggersData,
    isLoading: isLoadingTriggers,
    error: triggersError,
  } = useQuery({
    queryKey: [aiWorkflowQueryKeys.triggers],
    queryFn: () => api.aiWorkflows.getTriggers(),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: actionsData,
    isLoading: isLoadingActions,
    error: actionsError,
  } = useQuery({
    queryKey: [aiWorkflowQueryKeys.actions],
    queryFn: () => api.aiWorkflows.getActions(),
    staleTime: 5 * 60 * 1000,
  });

  const createWorkflowMutation = useMutation({
    mutationFn: (data: CreateWorkflowRequest) => api.aiWorkflows.createWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries([aiWorkflowQueryKeys.workflows]);
      onSave(workflow);
    },
    onError: (err: AppError) => {
      setError(err.message || 'Failed to create workflow. Please try again.');
    },
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: ({ workflowId, requestData }: { workflowId: number; requestData: CreateWorkflowRequest }) =>
      api.aiWorkflows.updateWorkflow({ workflowId, requestData }),
    onSuccess: () => {
      queryClient.invalidateQueries([aiWorkflowQueryKeys.workflows]);
      queryClient.invalidateQueries([aiWorkflowQueryKeys.workflow, existingWorkflow?.id]);
      onSave(workflow);
    },
    onError: (err: AppError) => {
      setError(err.message || 'Failed to update workflow. Please try again.');
    },
  });

  const triggers = triggersData?.triggers || [];
  const actions = actionsData?.actions || [];
  const isLoading = isLoadingTriggers || isLoadingActions;
  const isSaving = createWorkflowMutation.isLoading || updateWorkflowMutation.isLoading;

  const selectedTriggerCode = workflow.triggerCode;

  const applicableActions = actions.filter(
    (action) => !selectedTriggerCode || action.applicable_to_triggers.includes(selectedTriggerCode)
  );
  const applicableGroupedActions = groupActions(applicableActions);

  const handleStepClick = useCallback((step: WorkflowStep | null, type: StepType) => {
    if (!step) {
      if (type === 'trigger') {
        setPanelState({ mode: 'select_trigger' });
      } else if (type === 'action') {
        setPanelState({ mode: 'select_action' });
      }
      setSelectedStep(undefined);
    } else {
      setSelectedStep(step);
      if (type === 'trigger') {
        setPanelState({ mode: 'configure_trigger', selectedStep: step });
      } else if (type === 'decision') {
        setPanelState({ mode: 'configure_decision', selectedStep: step });
      } else if (type === 'action') {
        setPanelState({ mode: 'configure_action', selectedStep: step });
      }
    }
  }, []);

  const handleAddStep = useCallback((type: StepType) => {
    if (type === 'action') {
      setPanelState({ mode: 'select_action' });
    }
  }, []);

  const handleAddDecision = useCallback(() => {
    const newDecision: WorkflowStep = {
      id: uniqueId(),
      type: 'decision',
      order: workflow.trigger ? 2 : 1,
      isConfigured: false,
      data: {
        id: uniqueId(),
        prompt: '',
        isEnabled: true,
      } as DecisionNode,
    };
    setWorkflow((prev) => ({ ...prev, decision: newDecision }));
    setSelectedStep(newDecision);
    setPanelState({ mode: 'configure_decision', selectedStep: newDecision });
  }, [workflow.trigger]);

  const handleTriggerSelect = useCallback((trigger: ApiTrigger) => {
    const triggerStepData: TriggerStepData = {
      code: trigger.code,
      name: trigger.name,
      description: trigger.description,
    };
    const newTriggerStep: WorkflowStep = {
      id: uniqueId(),
      type: 'trigger',
      order: 1,
      isConfigured: false,
      data: triggerStepData,
    };
    setWorkflow((prev) => ({ 
      ...prev, 
      trigger: newTriggerStep,
      triggerCode: trigger.code,
    }));
    setSelectedStep(newTriggerStep);
    setPanelState({ mode: 'configure_trigger', selectedStep: newTriggerStep });
  }, []);

  const handleActionSelect = useCallback((action: ApiAction) => {
    const actionStepData: ActionStepData = {
      code: action.code,
      name: action.name,
      group: action.group,
    };
    const newActionStep: WorkflowStep = {
      id: uniqueId(),
      type: 'action',
      order: workflow.actions.length + (workflow.trigger ? 2 : 1) + (workflow.decision ? 1 : 0),
      isConfigured: false,
      data: actionStepData,
    };
    setWorkflow((prev) => ({
      ...prev,
      actions: [...prev.actions, newActionStep],
    }));
    setSelectedStep(newActionStep);
    setPanelState({ mode: 'configure_action', selectedStep: newActionStep });
  }, [workflow.actions.length, workflow.trigger, workflow.decision]);

  const handleTriggerConfigSave = useCallback((config: TriggerConfig) => {
    if (!workflow.trigger) return;
    
    const updatedTrigger: WorkflowStep = {
      ...workflow.trigger,
      isConfigured: true,
      data: {
        ...(workflow.trigger.data as TriggerStepData),
        config,
      },
    };
    setWorkflow((prev) => ({ 
      ...prev, 
      trigger: updatedTrigger,
      triggerScope: config.triggerScope,
      employeeList: config.employeeList || [],
      employeeNames: config.employeeNames || [],
    }));
    setSelectedStep(updatedTrigger);
    setPanelState({ mode: 'idle' });
  }, [workflow.trigger]);

  const handleDecisionConfigChange = useCallback((decision: DecisionNode) => {
    const updatedDecision: WorkflowStep = {
      id: workflow.decision?.id || uniqueId(),
      type: 'decision',
      order: 2,
      isConfigured: !!decision.prompt,
      data: decision,
    };
    setWorkflow((prev) => ({ ...prev, decision: updatedDecision }));
    setSelectedStep(updatedDecision);
  }, [workflow.decision?.id]);

  const handleDecisionConfigSave = useCallback((decision: DecisionNode) => {
    const updatedDecision: WorkflowStep = {
      id: workflow.decision?.id || uniqueId(),
      type: 'decision',
      order: 2,
      isConfigured: !!decision.prompt,
      data: decision,
    };
    setWorkflow((prev) => ({ ...prev, decision: updatedDecision }));
    setSelectedStep(updatedDecision);
    if (decision.prompt) {
      setPanelState({ mode: 'select_action' });
    }
  }, [workflow.decision?.id]);

  const handleActionConfigSave = useCallback((config: ActionConfig) => {
    if (!selectedStep || selectedStep.type !== 'action') return;
    
    const updatedActions = workflow.actions.map((action) => {
      if (action.id === selectedStep.id) {
        return {
          ...action,
          isConfigured: true,
          data: {
            ...(action.data as ActionStepData),
            config,
          },
        };
      }
      return action;
    });
    setWorkflow((prev) => ({ ...prev, actions: updatedActions }));
    setPanelState({ mode: 'idle' });
  }, [selectedStep, workflow.actions]);

  const handleDeleteStep = useCallback((step: WorkflowStep) => {
    if (step.type === 'action') {
      setWorkflow((prev) => ({
        ...prev,
        actions: prev.actions.filter((action) => action.id !== step.id),
      }));
      if (selectedStep?.id === step.id) {
        setSelectedStep(undefined);
        setPanelState({ mode: 'idle' });
      }
    } else if (step.type === 'decision') {
      setWorkflow((prev) => ({ ...prev, decision: null }));
      if (selectedStep?.type === 'decision') {
        setSelectedStep(undefined);
        setPanelState({ mode: 'idle' });
      }
    }
  }, [selectedStep]);

  const handleNameChange = useCallback((name: string) => {
    setWorkflow((prev) => ({ ...prev, name }));
  }, []);

  const handleSaveWorkflow = async () => {
    setError(null);
    const apiRequest = convertToApiRequest(workflow);
    
    if (existingWorkflow?.id) {
      updateWorkflowMutation.mutate({ workflowId: existingWorkflow.id, requestData: apiRequest });
    } else {
      createWorkflowMutation.mutate(apiRequest);
    }
  };

  const isWorkflowValid = workflow.trigger !== null && workflow.actions.length > 0;

  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%">
        <Spinner size="large" accessibilityLabel="Loading..." />
      </Box>
    );
  }

  if (triggersError || actionsError) {
    return (
      <Box padding="spacing.6">
        <Alert
          color="negative"
          description="Failed to load workflow configuration. Please try again."
          isDismissible
          onDismiss={onCancel}
        />
      </Box>
    );
  }

  const triggerData = workflow.trigger?.data as TriggerStepData | undefined;

  return (
    <Box display="flex" height="100%" width="100%">
      <Box flexGrow={1} display="flex" flexDirection="column" overflow="hidden">
        <Box
          padding="spacing.5"
          borderBottomWidth="thin"
          borderBottomColor="surface.border.gray.normal"
          backgroundColor="surface.background.gray.subtle">
          <Box display="flex" alignItems="center" gap="spacing.3">
            {isEditingName ? (
              <TextInput
                label=""
                value={workflow.name}
                onChange={({ value }) => handleNameChange(value || '')}
                onBlur={() => setIsEditingName(false)}
                autoFocus
              />
            ) : (
              <ClickableWrapper onClick={() => setIsEditingName(true)}>
                <Text size="large" weight="semibold">
                  {workflow.name}
                </Text>
                <EditIcon size="medium" color="interactive.icon.gray.normal" />
              </ClickableWrapper>
            )}
          </Box>
        </Box>

        {error && (
          <Box padding="spacing.4">
            <Alert
              color="negative"
              description={error}
              isDismissible
              onDismiss={() => setError(null)}
            />
          </Box>
        )}

        <Box flexGrow={1} overflowY="auto">
          <WorkflowCanvas
            workflow={workflow}
            selectedStep={selectedStep}
            onStepClick={handleStepClick}
            onAddStep={handleAddStep}
            onAddDecision={handleAddDecision}
            onDeleteStep={handleDeleteStep}
          />
        </Box>

        <Box
          padding="spacing.5"
          borderTopWidth="thin"
          borderTopColor="surface.border.gray.normal"
          backgroundColor="surface.background.gray.subtle"
          display="flex"
          justifyContent="center"
          gap="spacing.4">
          <Button
            variant="tertiary"
            onClick={onCancel}
            isDisabled={isSaving}>
            Back
          </Button>
          <Button
            onClick={handleSaveWorkflow}
            isLoading={isSaving}
            isDisabled={!isWorkflowValid}>
            Save
          </Button>
        </Box>
      </Box>

      <RightPanelContainer>
        <RightPanel
          panelState={panelState}
          triggers={triggers}
          actions={applicableActions}
          groupedActions={applicableGroupedActions}
          selectedStep={selectedStep}
          triggerCode={triggerData?.code}
          onTriggerSelect={handleTriggerSelect}
          onActionSelect={handleActionSelect}
          onTriggerConfigSave={handleTriggerConfigSave}
          onDecisionConfigChange={handleDecisionConfigChange}
          onDecisionConfigSave={handleDecisionConfigSave}
          onActionConfigSave={handleActionConfigSave}
        />
      </RightPanelContainer>

      <Modal isOpen={showDeleteModal} onDismiss={() => setShowDeleteModal(false)}>
        <ModalHeader title="Delete Step" />
        <ModalBody>
          <Text>Are you sure you want to delete this step?</Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" justifyContent="flex-end">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              color="negative"
              onClick={() => {
                setShowDeleteModal(false);
              }}>
              Delete
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export { WorkflowBuilder };
